import { useEffect, useState } from 'react'

import { api } from './api'
import { getStoredPrincipal } from './apiAuth'
import { dexieOperatorRosterRepo } from '../offline/repos/DexieOperatorRosterRepo'
import { ensureOfflineReady } from '../offline/initOffline'

// Findings #55: show "who logged / corrected it" by name instead of an actor
// id. Names come from the Crew Mode operator roster (cached on the device,
// viewer-readable) and, when the viewer may list team members (admin), from
// the team member labels. Unknown ids fall back to the id itself.

const CACHE_TTL_MS = 5 * 60 * 1000

let cached: { at: number; names: Map<string, string> } | null = null
let inflight: Promise<Map<string, string>> | null = null

const load = async (): Promise<Map<string, string>> => {
  const names = new Map<string, string>()

  try {
    await ensureOfflineReady()
    for (const entry of await dexieOperatorRosterRepo.list()) {
      if (entry.actorId && entry.displayName) names.set(entry.actorId, entry.displayName)
    }
  } catch {
    // no local roster yet
  }

  // Team members are admin-only; don't ask (and log a 403) for anyone else.
  const isAdmin = getStoredPrincipal()?.role === 'admin'
  const [roster, members] = await Promise.allSettled([
    api.teamMembers.operatorRoster(),
    isAdmin ? api.teamMembers.list() : Promise.resolve([])
  ])
  if (roster.status === 'fulfilled') {
    for (const entry of roster.value) {
      if (entry.actorId && entry.displayName) names.set(entry.actorId, entry.displayName)
    }
  }
  if (members.status === 'fulfilled') {
    for (const member of members.value) {
      if (member.actorId && member.label && !names.has(member.actorId)) names.set(member.actorId, member.label)
    }
  }
  return names
}

export const loadActorNames = async (): Promise<Map<string, string>> => {
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached.names
  if (!inflight) {
    inflight = load()
      .then((names) => {
        cached = { at: Date.now(), names }
        return names
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

export const resetActorNamesCacheForTests = (): void => {
  cached = null
  inflight = null
}

export const actorLabel = (names: Map<string, string> | null, actorId: string | undefined | null): string => {
  if (!actorId) return ''
  return names?.get(actorId) ?? actorId
}

export const useActorNames = (): Map<string, string> | null => {
  const [names, setNames] = useState<Map<string, string> | null>(cached?.names ?? null)
  useEffect(() => {
    let active = true
    void loadActorNames()
      .then((loaded) => {
        if (active) setNames(loaded)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [])
  return names
}
